import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { arrayOf, func, node, number, PropTypes, shape, string } from 'prop-types';

// Components
import { Box, Button, CircularProgress, LinearProgress, Typography } from '@mui/material';

import { DrawerLayoutWrapper } from '.';

const Wizard = ({ title, onClose, steps, stepHeaderSx }) => {
  const dispatch = useDispatch();
  const initialRender = useRef(true);
  const [currentStepIdx, setCurrentStepIdx] = useState(0);

  const initialLoadingData = useSelector(steps[currentStepIdx].loading);
  const loadingData = useSelector(steps[currentStepIdx].showSpinner);
  const disabled = useSelector(steps[currentStepIdx].disabled);
  const stepLabel = useSelector(steps[currentStepIdx].stepLabel);
  const totalSteps = steps.length - 1;
  const stepId = steps?.[currentStepIdx]?.id;

  const progress = stepId ? (stepId / steps.length) * 100 : 0;

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    !loadingData && currentStepIdx < totalSteps && setCurrentStepIdx(currentStepIdx + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingData]);

  const handleRightButtonClick = () =>
    currentStepIdx < totalSteps
      ? steps[currentStepIdx].goNext(dispatch, setCurrentStepIdx, currentStepIdx)
      : onClose();

  const handleLeftButtonClick = () => {
    if (steps[currentStepIdx].goBack(dispatch, setCurrentStepIdx) || !currentStepIdx) return;
    setCurrentStepIdx(currentStepIdx - 1);
  };

  const buttons = (
    <Box alignItems="center" display="flex" justifyContent="center" width="100%">
      {!!steps[currentStepIdx].leftLabel && (
        <Button
          disabled={loadingData}
          fullWidth={currentStepIdx > 0}
          onClick={handleLeftButtonClick}
          sx={{
            marginRight: currentStepIdx > 0 ? 2 : 0,
          }}
          variant="outlined"
        >
          {loadingData && (
            <CircularProgress
              size={14}
              sx={{
                bottom: 0,
                color: 'secondary.main',
                left: 0,
                margin: 'auto',
                pointerEvents: 'none',
                position: 'absolute',
                right: 0,
                top: 0,
              }}
            />
          )}
          {steps[currentStepIdx].leftLabel}
        </Button>
      )}
      {!!steps[currentStepIdx].rightLabel && (
        <Button
          disabled={disabled || loadingData}
          fullWidth={currentStepIdx > 0}
          onClick={handleRightButtonClick}
        >
          {loadingData && (
            <CircularProgress
              size={14}
              sx={{
                bottom: 0,
                color: 'secondary.main',
                left: 0,
                margin: 'auto',
                pointerEvents: 'none',
                position: 'absolute',
                right: 0,
                top: 0,
              }}
            />
          )}
          {steps[currentStepIdx].rightLabel}
        </Button>
      )}
    </Box>
  );

  return (
    <DrawerLayoutWrapper
      buttons={buttons}
      headerButtonText="CLOSE"
      isLoading={initialLoadingData}
      onHeaderButtonClick={onClose}
      title={title}
    >
      <>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          p={3}
          sx={{ ...stepHeaderSx }}
          w={1}
        >
          <LinearProgress
            data-variant="magellanProgressBar"
            value={progress}
            variant="determinate"
          />
          <Typography variant="caption">{`${stepLabel} (${stepId} of ${steps.length})`}</Typography>
        </Box>
        {steps[currentStepIdx].component}
      </>
    </DrawerLayoutWrapper>
  );
};

export default Wizard;

Wizard.propTypes = {
  onClose: func.isRequired,

  stepHeaderSx: PropTypes.shape({}),
  steps: arrayOf(
    shape({
      component: node,
      disabled: func,
      goBack: func,
      goNext: func,
      id: number,
      leftLabel: string,
      rightLabel: string,
      showSpinner: func,
    })
  ).isRequired,
  title: string.isRequired,
};
Wizard.defaultProps = {
  stepHeaderSx: {},
};
